// The live frame stream of one share: /v1/shares/{id}/ws?since={lastSeq}.
//
// It reconnects with exponential backoff (1 s doubling, at most 30 s),
// resuming from the last seq it saw, and drops a seq it has already
// delivered — the relay's backlog replay and a frame in flight at the
// disconnect may both carry it. Pings are answered; acks go back at most
// once a second. Nothing here knows what a frame means beyond that.
import { openLive } from "./api";
import { decode, encode, frame, type Actor, type Envelope } from "./protocol";

export const BackoffBaseMs = 1000;
export const BackoffMaxMs = 30_000;
export const AckEveryMs = 1000;

/** The delay before reconnect attempt n (0-based), with jitter in [d/2, d]. */
export function backoffDelay(attempt: number, random: () => number = Math.random): number {
  const d = Math.min(BackoffMaxMs, BackoffBaseMs * 2 ** Math.min(attempt, 30));
  return Math.round(d / 2 + (d / 2) * random());
}

/** The minimal WebSocket surface used, so a test can hand in a fake. */
export interface SocketLike {
  readyState: number;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number; reason?: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

export type ConnState =
  | { kind: "connecting" }
  | { kind: "open" }
  | { kind: "waiting"; retryInMs: number; attempt: number }
  | { kind: "closed"; reason: string };

export interface LiveOptions {
  /** The socket URL for a given resume point. */
  url: (since: number) => string;
  /** Who this browser is, for the frames it sends (its role and user id). */
  me: Actor;
  /** The share this socket is about; absent on a class socket. */
  share?: string;
  /**
   * A class socket (`/v1/classes/{id}/ws`) carries many shares' frames, and
   * seqs are per share: so nothing is deduplicated or acked by seq, and one
   * share closing does not stop it. Whoever reads it reloads on each open.
   */
  fanIn?: boolean;
  onFrame: (e: Envelope) => void;
  onState?: (s: ConnState) => void;
  onBadFrame?: (err: unknown, raw: string) => void;
  /** Opens the socket; by default with a ticket from the main server (openLive). */
  socket?: (url: string) => SocketLike | Promise<SocketLike>;
  random?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (id: unknown) => void;
}

/** Close codes after which reconnecting is pointless: 4410 is a share that has ended (Go `server.CloseShareEnded`). */
export const TerminalCloseCodes = new Set([4401, 4403, 4404, 4410]);

const OPEN = 1;
const SeenWindow = 4096;

export class LiveConnection {
  private ws: SocketLike | null = null;
  private attempt = 0;
  private retryTimer: unknown = null;
  private ackTimer: unknown = null;
  private stopped = false;
  private seen = new Set<number>();
  private acked = 0;
  lastSeq = 0;

  constructor(private readonly o: LiveOptions) {}

  start(): void {
    this.stopped = false;
    this.open();
  }

  /** Stop for good: no reconnect. */
  stop(reason = "stopped"): void {
    this.stopped = true;
    this.clear();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      ws.close(1000, reason);
    }
    this.o.onState?.({ kind: "closed", reason });
  }

  /** Send a frame as this browser; false when the socket is not open. */
  send(e: Envelope): boolean {
    if (!this.ws || this.ws.readyState !== OPEN) return false;
    this.ws.send(encode(e));
    return true;
  }

  get connected(): boolean {
    return this.ws?.readyState === OPEN;
  }

  private timer(fn: () => void, ms: number): unknown {
    return (this.o.setTimer ?? ((f, m) => setTimeout(f, m)))(fn, ms);
  }

  private clear(): void {
    const clr = this.o.clearTimer ?? ((id) => clearTimeout(id as ReturnType<typeof setTimeout>));
    if (this.retryTimer !== null) clr(this.retryTimer);
    if (this.ackTimer !== null) clr(this.ackTimer);
    this.retryTimer = null;
    this.ackTimer = null;
  }

  private open(): void {
    if (this.stopped) return;
    this.o.onState?.({ kind: "connecting" });
    const make = this.o.socket ?? ((u: string) => openLive(u) as unknown as Promise<SocketLike>);
    const made = make(this.o.url(this.lastSeq));
    if (made instanceof Promise) {
      // no ticket is a connection lost: tried again, as a socket refused is
      made.then(
        (ws) => this.attach(ws),
        () => {
          if (!this.stopped) this.schedule();
        },
      );
      return;
    }
    this.attach(made);
  }

  private attach(ws: SocketLike): void {
    if (this.stopped) {
      ws.close(1000);
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.attempt = 0;
      this.o.onState?.({ kind: "open" });
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data === "string") this.receive(ev.data);
    };
    ws.onerror = () => {
      // onclose follows and decides.
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.stopped) return;
      if (TerminalCloseCodes.has(ev.code)) {
        this.stop(ev.reason || `closed (${ev.code})`);
        return;
      }
      this.schedule();
    };
  }

  private schedule(): void {
    const ms = backoffDelay(this.attempt, this.o.random);
    this.o.onState?.({ kind: "waiting", retryInMs: ms, attempt: this.attempt });
    this.attempt += 1;
    this.retryTimer = this.timer(() => {
      this.retryTimer = null;
      this.open();
    }, ms);
  }

  private receive(raw: string): void {
    let e: Envelope;
    try {
      e = decode(raw);
    } catch (err) {
      this.o.onBadFrame?.(err, raw);
      return;
    }
    if (e.type === "ping") {
      this.send(frame("pong", this.o.me, null, this.o.share));
      return;
    }
    if (this.o.fanIn) {
      this.o.onFrame(e);
      return;
    }
    if (e.seq !== undefined) {
      if (this.seen.has(e.seq) || e.seq <= this.lastSeq - SeenWindow) return;
      this.seen.add(e.seq);
      if (e.seq > this.lastSeq) this.lastSeq = e.seq;
      if (this.seen.size > SeenWindow * 2) {
        for (const s of this.seen) if (s <= this.lastSeq - SeenWindow) this.seen.delete(s);
      }
      this.scheduleAck();
    }
    this.o.onFrame(e);
    if (e.type === "share.close") this.stop(e.body.reason || "closed");
  }

  private scheduleAck(): void {
    if (this.ackTimer !== null) return;
    this.ackTimer = this.timer(() => {
      this.ackTimer = null;
      if (this.lastSeq > this.acked && this.send(frame("ack", this.o.me, { seq: this.lastSeq }, this.o.share))) {
        this.acked = this.lastSeq;
      }
    }, AckEveryMs);
  }
}
